import { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import {
  ASSIGNABLE_ROLE_KINDS,
  SHARED_ITEM_INVITE_STATUSES,
  SHARED_ITEM_KIND_SELECTABLE,
} from "@/pages/utilities/shared-item-invites/shared/constants";
import { toastModule } from "@/modules/toast";
import { usePrimarySpaceAccountId } from "@/pages/utilities/shared-item-invites/shared/use-primary-space-account-id";

export const SharedItemInvitesCreatePage = () => {
  const navigate = useNavigate();
  const [inviterSpaceAccountId, setInviterSpaceAccountId] = useState("");
  const [sharedItemId, setSharedItemId] = useState("");
  const [sharedItemKind, setSharedItemKind] = useState<string>("NOTE");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [role, setRole] = useState<string>("EDITOR");
  const [expiresAt, setExpiresAt] = useState<string>(() => {
    const now = new Date();
    now.setDate(now.getDate() + 7);
    const pad = (value: number) => value.toString().padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  });
  const [status, setStatus] = useState<string>("PENDING");
  const [includeStatusOverride, setIncludeStatusOverride] = useState(false);
  const [acceptedBySpaceAccountId, setAcceptedBySpaceAccountId] = useState("");
  const [acceptedAt, setAcceptedAt] = useState("");
  const [isRevoked, setIsRevoked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { primarySpaceAccountId, isLoading: isPrimaryLoading } = usePrimarySpaceAccountId();

  const resetForm = () => {
    setInviterSpaceAccountId("");
    setSharedItemId("");
    setSharedItemKind("NOTE");
    setInviteeEmail("");
    setRole("VIEWER");
    setExpiresAt("");
    setStatus("PENDING");
    setIncludeStatusOverride(false);
    setAcceptedBySpaceAccountId("");
    setAcceptedAt("");
    setIsRevoked(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedInviter = inviterSpaceAccountId.trim();
    const trimmedItemId = sharedItemId.trim();
    const trimmedEmail = inviteeEmail.trim();

    if (!trimmedInviter || !trimmedItemId || !trimmedEmail) {
      toastModule.triggerToast({
        content: "Inviter, shared item ID, and invitee email are required.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        inviter_space_account_id: trimmedInviter,
        shared_item_id: trimmedItemId,
        shared_item_kind: sharedItemKind,
        invitee_email: trimmedEmail,
        role,
      };

      if (expiresAt) {
        body.expires_at = new Date(expiresAt).toISOString();
      }
      if (includeStatusOverride) {
        body.status = status;
        body.is_revoked = isRevoked;
      }
      if (acceptedBySpaceAccountId.trim()) {
        body.accepted_by_space_account_id = acceptedBySpaceAccountId.trim();
      }
      if (acceptedAt) {
        body.accepted_at = new Date(acceptedAt).toISOString();
      }

      const response = await api.post("/internal/v1/shared-item-invites", {
        body: {
          inviter_space_account_id: trimmedInviter,
          shared_item_id: trimmedItemId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          shared_item_kind: sharedItemKind as any,
          invitee_email: trimmedEmail,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          role: role as any,
        },
      });

      if (!response.data) {
        throw new Error("Failed to create invite");
      }

      toastModule.triggerToast({ content: "Invite created." });
      resetForm();
      navigate(atlasRoutes.sharedItemInvitesView({ params: { inviteId: response.data.id } }).path);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create invite";
      toastModule.triggerToast({ content: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Create Shared Item Invite</Title>
      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="inviter-space-account-id">Inviter Space Account ID</Label>
          <Row>
            <Input
              id="inviter-space-account-id"
              value={inviterSpaceAccountId}
              onChange={event => setInviterSpaceAccountId(event.target.value)}
              placeholder="Inviter space account UUID"
              required
            />
            <Button
              type="button"
              variant="soft"
              onClick={() =>
                primarySpaceAccountId && setInviterSpaceAccountId(primarySpaceAccountId)
              }
              disabled={!primarySpaceAccountId || isPrimaryLoading}
            >
              Fill space account
            </Button>
          </Row>
        </Field>

        <Field>
          <Label htmlFor="shared-item-id">Shared Item ID</Label>
          <Input
            id="shared-item-id"
            value={sharedItemId}
            onChange={event => setSharedItemId(event.target.value)}
            placeholder="Note or collection UUID"
            required
          />
        </Field>

        <Field>
          <Label htmlFor="shared-item-kind">Shared Item Kind</Label>
          <Select
            id="shared-item-kind"
            value={sharedItemKind}
            onChange={event => setSharedItemKind(event.target.value)}
          >
            {SHARED_ITEM_KIND_SELECTABLE.map(kind => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label htmlFor="invitee-email">Invitee Email</Label>
          <Input
            id="invitee-email"
            type="email"
            value={inviteeEmail}
            onChange={event => setInviteeEmail(event.target.value)}
            placeholder="person@example.com"
            required
          />
        </Field>

        <Field>
          <Label htmlFor="role">Role</Label>
          <Select id="role" value={role} onChange={event => setRole(event.target.value)}>
            {ASSIGNABLE_ROLE_KINDS.map(roleValue => (
              <option key={roleValue} value={roleValue}>
                {roleValue}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label htmlFor="expires-at">Expires At</Label>
          <Input
            id="expires-at"
            type="datetime-local"
            value={expiresAt}
            onChange={event => setExpiresAt(event.target.value)}
          />
        </Field>

        <AdvancedSection>
          <Summary>Advanced Options</Summary>
          <AdvancedContent>
            <CheckboxRow>
              <input
                id="status-override"
                type="checkbox"
                checked={includeStatusOverride}
                onChange={event => setIncludeStatusOverride(event.target.checked)}
              />
              <span>Override default status and revoked flag</span>
            </CheckboxRow>

            {includeStatusOverride && (
              <AdvancedGrid>
                <Field>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    id="status"
                    value={status}
                    onChange={event => setStatus(event.target.value)}
                  >
                    {SHARED_ITEM_INVITE_STATUSES.map(statusValue => (
                      <option key={statusValue} value={statusValue}>
                        {statusValue}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label htmlFor="revoked-flag">Revoked</Label>
                  <CheckboxRow>
                    <input
                      id="revoked-flag"
                      type="checkbox"
                      checked={isRevoked}
                      onChange={event => setIsRevoked(event.target.checked)}
                    />
                    <span>Mark invite as revoked</span>
                  </CheckboxRow>
                </Field>
              </AdvancedGrid>
            )}

            <AdvancedGrid>
              <Field>
                <Label htmlFor="accepted-by-space-account-id">Accepted By Space Account ID</Label>
                <Input
                  id="accepted-by-space-account-id"
                  value={acceptedBySpaceAccountId}
                  onChange={event => setAcceptedBySpaceAccountId(event.target.value)}
                  placeholder="Optional"
                />
              </Field>
              <Field>
                <Label htmlFor="accepted-at">Accepted At</Label>
                <Input
                  id="accepted-at"
                  type="datetime-local"
                  value={acceptedAt}
                  onChange={event => setAcceptedAt(event.target.value)}
                />
              </Field>
            </AdvancedGrid>
          </AdvancedContent>
        </AdvancedSection>

        <ButtonRow>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create Invite"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(atlasRoutes.sharedItemInvites({}).path)}
          >
            Cancel
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
`;

const Label = styled.label`
  font-size: 14px;
  color: var(--gray-11);
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--gray-a5);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--gray-a5);
  border-radius: 6px;
  font-size: 14px;
  background: var(--color-surface);
`;

const AdvancedSection = styled.details`
  border: 1px solid var(--gray-a5);
  border-radius: 8px;
  padding: 12px 16px;
`;

const Summary = styled.summary`
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
`;

const AdvancedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AdvancedGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > *:first-of-type {
    flex: 1;
  }
`;
